import { useState, useEffect } from 'react';
import { useRestaurant } from '../../contexts/RestaurantContext';
import { useAuth } from '../../contexts/AuthContext';
import { useCustomers } from '../../hooks/useCustomers';
import { useTables } from '../../hooks/useTables';
import { settingsService } from '../../services/settings.service';
import { hasEffectivePermission } from '../../utils/rbac';
import {
  LayoutDashboard,
  ShoppingBag,
  UtensilsCrossed,
  Users,
  Star,
  BarChart3,
  Settings,
  Shield,
  Building2,
  X,
  UserCog,
  ChevronRight,
  CreditCard,
  Table2,
  Plus,
  Info,
  CheckSquare,
  HelpCircle,
  FileText,
  Mail,
} from 'lucide-react';

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
  activePage?: string;
  onNavigate?: (page: string) => void;
  ordersCount?: number;
}

export function Sidebar({ isOpen, onClose, activePage = 'dashboard', onNavigate, ordersCount = 0 }: SidebarProps) {
  // Initialize customersExpanded from localStorage, default to false (collapsed)
  const [customersExpanded, setCustomersExpanded] = useState(() => {
    const stored = localStorage.getItem('sidebar_customersExpanded');
    return stored !== null ? stored === 'true' : false;
  });

  const [messagingExpanded, setMessagingExpanded] = useState(() => {
    const stored = localStorage.getItem('sidebar_messagingExpanded');
    if (stored !== null) return stored === 'true';
    return ['message-batches', 'message-templates', 'create-batch', 'providers'].includes(activePage);
  });

  const [isRestaurant, setIsRestaurant] = useState(() => {
    const stored = localStorage.getItem('isRestaurant');
    return stored !== null ? stored === 'true' : true;
  });

  // Load sidebar menu visibility settings - start with localStorage cache, then load from DB
  const [menuVisibility, setMenuVisibility] = useState(() => {
    const stored = localStorage.getItem('sidebarMenuVisibility');
    return stored ? JSON.parse(stored) : {
      dashboard: true,
      orders: true,
      menus: true,
      customers: true,
      reviews: true,
      analytics: true,
      reports: true,
      users: true,
      messaging: true,
      settings: true,
    };
  });

  // Load sidebar visibility from database on mount
  useEffect(() => {
    const loadSidebarVisibility = async () => {
      try {
        const response = await settingsService.getTenantSettings();
        if (response.data?.sidebarMenuVisibility) {
          setMenuVisibility(response.data.sidebarMenuVisibility);
          // Update localStorage cache
          localStorage.setItem('sidebarMenuVisibility', JSON.stringify(response.data.sidebarMenuVisibility));
        }
        if (response.data) {
          const enabled = response.data.isRestaurant ?? true;
          setIsRestaurant(enabled);
          localStorage.setItem('isRestaurant', String(enabled));
        }
      } catch (error) {
        console.error('Failed to load sidebar visibility:', error);
        // Keep using cached/default values
      }
    };

    loadSidebarVisibility();
  }, []);
  
  const { restaurantName, restaurantLogo } = useRestaurant();
  const { user, isInitialized } = useAuth();

  // Debug: Log user data on component mount and when user changes
  useEffect(() => {
    console.log('🔍 Sidebar - User Data:', {
      user,
      role: user?.role,
      permissions: user?.permissions,
      hasUser: !!user,
      isInitialized
    });
    
    // Debug permission checks
    const permissionsToCheck = ['dashboard', 'orders', 'menus', 'tables', 'customers', 'reviews', 'analytics', 'reports', 'users', 'settings'];
    const permissionResults: any = {};
    
    const effectiveRole = user?.role || 'admin';
    
    permissionsToCheck.forEach(perm => {
      permissionResults[perm] = hasEffectivePermission(user, perm);
    });
    
    console.log('🔍 Sidebar - Permission Check Results:', permissionResults);
    console.log('🔍 Sidebar - Using effectiveRole:', effectiveRole);
    console.log('🔍 Sidebar - User role:', user?.role);
  }, [user, isInitialized]);

  // Persist customersExpanded state to localStorage
  useEffect(() => {
    localStorage.setItem('sidebar_customersExpanded', String(customersExpanded));
  }, [customersExpanded]);

  // Persist messagingExpanded state to localStorage
  useEffect(() => {
    localStorage.setItem('sidebar_messagingExpanded', String(messagingExpanded));
  }, [messagingExpanded]);

  // Automatically open messaging submenu when a messaging page is active
  useEffect(() => {
    if (['message-batches', 'message-templates', 'create-batch', 'providers'].includes(activePage)) {
      setMessagingExpanded(true);
    }
  }, [activePage]);

  // Listen for changes to sidebar visibility settings
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'sidebarMenuVisibility' && e.newValue) {
        setMenuVisibility(JSON.parse(e.newValue));
      }
      if (e.key === 'isRestaurant' && e.newValue !== null) {
        setIsRestaurant(e.newValue === 'true');
      }
    };

    // Also listen for custom event (for same-tab updates)
    const handleCustomEvent = () => {
      const stored = localStorage.getItem('sidebarMenuVisibility');
      if (stored) {
        setMenuVisibility(JSON.parse(stored));
      }
      const storedIsRestaurant = localStorage.getItem('isRestaurant');
      if (storedIsRestaurant !== null) {
        setIsRestaurant(storedIsRestaurant === 'true');
      }
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('sidebarVisibilityChanged', handleCustomEvent);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('sidebarVisibilityChanged', handleCustomEvent);
    };
  }, []);

  // Live customer counts from API
  const { customers: allCustomers } = useCustomers();
  const generalCount = allCustomers.filter(
    (c) => !['SILVER', 'GOLD', 'PLATINUM'].includes(c.membershipTier)
  ).length;
  const membersCount = allCustomers.filter(
    (c) => ['SILVER', 'GOLD', 'PLATINUM'].includes(c.membershipTier)
  ).length;

  // Live table counts from API
  const { tables: allTables } = useTables();
  const availableTablesCount = allTables.filter(t => t.status === 'available').length;
  const occupiedTablesCount = allTables.filter(t => t.status === 'occupied').length;

  const handleNavigation = (page: string) => {
    if (onNavigate) {
      onNavigate(page);
    }
    // Auto-close sidebar on mobile after navigation
    if (window.innerWidth < 768) {
      onClose();
    }
  };

  // If user data hasn't loaded yet, show a loading state
  // This prevents the menu from showing only viewer permissions before user data loads
  if (!user && isInitialized) {
    console.log('🔍 Sidebar - No user found after initialization');
  }

  // Use the user's actual role, or 'admin' as fallback to show all menus while loading
  // This prevents menu items from disappearing during user data loading
  const effectiveRole = user?.role || 'admin';
  const hasCustomersAccess = hasEffectivePermission(user, 'customers', true);
  const hasTablesAccess = hasEffectivePermission(user, 'tables', true);
  const hasTransactionsAccess = hasEffectivePermission(user, 'transactions', true);

  return (
    <>
      {/* Sidebar */}
      <div className={`
        fixed
        inset-y-0 left-0
        w-64 bg-white dark:bg-gray-900 
        border-r border-gray-100 dark:border-gray-800 
        flex flex-col
        transform transition-transform duration-300 ease-in-out
        z-50
        shadow-2xl min-[768px]:shadow-none
        ${isOpen ? 'translate-x-0' : '-translate-x-full'}
      `}>
        <div className="p-4 border-b border-gray-100 dark:border-gray-800">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {restaurantLogo ? (
                <img 
                  src={restaurantLogo} 
                  alt={restaurantName}
                  className="w-10 h-10 rounded-xl object-cover"
                />
              ) : (
                <div className="w-10 h-10 bg-indigo-600 rounded-xl flex items-center justify-center">
                  <svg className="w-6 h-6 text-white" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 2L2 7v10c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V7l-10-5z" />
                  </svg>
                </div>
              )}
              <span className="font-bold text-gray-900 dark:text-white text-xl">{restaurantName}</span>
            </div>
            <button 
              onClick={onClose}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors lg:block"
              aria-label="Close sidebar"
            >
              <X className="w-5 h-5 text-gray-600 dark:text-gray-400" />
            </button>
          </div>
        </div>

        <nav className="flex-1 p-4 space-y-2 overflow-y-auto min-h-0 scrollbar-hide">
          {hasEffectivePermission(user, 'dashboard', true) && menuVisibility.dashboard && (
            <a
              href="#"
              onClick={(e) => { e.preventDefault(); handleNavigation('dashboard'); }}
              className={`group relative flex items-center gap-3 px-4 py-3 rounded-lg border-l-4 ${
                activePage === 'dashboard' 
                  ? 'text-indigo-600 dark:text-indigo-400 border-indigo-600' 
                  : 'text-gray-400 dark:text-gray-500 border-transparent hover:bg-gray-50 dark:hover:bg-gray-800 hover:text-gray-700 dark:hover:text-gray-300'
              } transition-colors`}
            >
              <LayoutDashboard className="w-5 h-5" />
              <span className="font-semibold text-sm">Dashboard</span>
            </a>
          )}

          {hasEffectivePermission(user, 'orders', true) && menuVisibility.orders && (
            <a
              href="#"
              onClick={(e) => { e.preventDefault(); handleNavigation('orders'); }}
              className={`group relative flex items-center gap-3 px-4 py-3 rounded-lg border-l-4 ${
                activePage === 'orders'
                  ? 'text-indigo-600 dark:text-indigo-400 border-indigo-600'
                  : 'text-gray-400 dark:text-gray-500 border-transparent hover:bg-gray-50 dark:hover:bg-gray-800 hover:text-gray-700 dark:hover:text-gray-300'
              } transition-colors`}
            >
              <ShoppingBag className="w-5 h-5" />
              <span className="font-semibold text-sm">Orders</span>
              <span className="ml-auto bg-indigo-600 text-white text-xs px-2 py-0.5 rounded-full">{ordersCount}</span>
            </a>
          )}

          {hasEffectivePermission(user, 'menus', true) && menuVisibility.menus && (
            <a
              href="#"
              onClick={(e) => { e.preventDefault(); handleNavigation('menuTypes'); }}
              className={`group relative flex items-center gap-3 px-4 py-3 rounded-lg border-l-4 ${
                activePage === 'menus' || activePage === 'menuTypes'
                  ? 'text-indigo-600 dark:text-indigo-400 border-indigo-600'
                  : 'text-gray-400 dark:text-gray-500 border-transparent hover:bg-gray-50 dark:hover:bg-gray-800 hover:text-gray-700 dark:hover:text-gray-300'
              } transition-colors`}
            >
              <UtensilsCrossed className="w-5 h-5" />
              <span className="font-semibold text-sm">Menus</span>
            </a>
          )}

          {(hasCustomersAccess || hasTablesAccess || (!isRestaurant && hasTransactionsAccess)) && menuVisibility.customers && (
            <div>
              <button
                onClick={() => {
                  setCustomersExpanded(!customersExpanded);
                  handleNavigation(hasCustomersAccess ? 'customers' : 'tables');
                }}
                className={`group relative w-full flex items-center gap-3 px-4 py-3 rounded-lg border-l-4 ${
                  activePage === 'customers' || activePage === 'customers-general' || activePage === 'customers-members' || activePage === 'tables' || activePage === 'transactions'
                    ? 'text-indigo-600 dark:text-indigo-400 border-indigo-600'
                    : 'text-gray-400 dark:text-gray-500 border-transparent hover:bg-gray-50 dark:hover:bg-gray-800 hover:text-gray-700 dark:hover:text-gray-300'
                } transition-colors`}
              >
                <Users className="w-5 h-5" />
                <span className="font-semibold text-sm">Customers</span>
                <ChevronRight className={`w-4 h-4 ml-auto transition-transform ${customersExpanded ? 'rotate-90' : ''}`} />
              </button>
              
              {customersExpanded && (
                <div className="ml-12 mt-1 space-y-1">
                  {hasCustomersAccess && (
                  <a 
                    href="#" 
                    onClick={(e) => { e.preventDefault(); handleNavigation('customers-general'); }}
                    className={`group relative flex items-center justify-between px-4 py-2 text-sm hover:text-gray-700 dark:hover:text-gray-300 transition-colors font-semibold rounded-lg ${
                      activePage === 'customers-general' 
                        ? 'text-indigo-600 dark:text-indigo-400 bg-indigo-50 dark:bg-indigo-900/30' 
                        : 'text-gray-400 dark:text-gray-500 hover:bg-gray-50 dark:hover:bg-gray-800'
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <UserCog className="w-4 h-4" />
                      <span>General</span>
                    </div>
                    <span className="text-xs bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-400 px-2 py-0.5 rounded-full">
                      {generalCount}
                    </span>
                  </a>
                  )}
                  {hasCustomersAccess && (
                  <a 
                    href="#" 
                    onClick={(e) => { e.preventDefault(); handleNavigation('customers-members'); }}
                    className={`group relative flex items-center justify-between px-4 py-2 text-sm hover:text-gray-700 dark:hover:text-gray-300 transition-colors font-semibold rounded-lg ${
                      activePage === 'customers-members' 
                        ? 'text-indigo-600 dark:text-indigo-400 bg-indigo-50 dark:bg-indigo-900/30' 
                        : 'text-gray-400 dark:text-gray-500 hover:bg-gray-50 dark:hover:bg-gray-800'
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <Star className="w-4 h-4" />
                      <span>Members</span>
                    </div>
                    <span className="text-xs bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 px-2 py-0.5 rounded-full">
                      {membersCount}
                    </span>
                  </a>
                  )}
                  {!isRestaurant && hasTransactionsAccess && (
                  <a
                    href="#"
                    onClick={(e) => { e.preventDefault(); handleNavigation('transactions'); }}
                    className={`group relative flex items-center justify-between px-4 py-2 text-sm hover:text-gray-700 dark:hover:text-gray-300 transition-colors font-semibold rounded-lg ${
                      activePage === 'transactions'
                        ? 'text-indigo-600 dark:text-indigo-400 bg-indigo-50 dark:bg-indigo-900/30'
                        : 'text-gray-400 dark:text-gray-500 hover:bg-gray-50 dark:hover:bg-gray-800'
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <CreditCard className="w-4 h-4" />
                      <span>Transactions</span>
                    </div>
                  </a>
                  )}
                  {hasTablesAccess && isRestaurant && (
                  <a 
                    href="#" 
                    onClick={(e) => { e.preventDefault(); handleNavigation('tables'); }}
                    className={`group relative flex items-center justify-between px-4 py-2 text-sm hover:text-gray-700 dark:hover:text-gray-300 transition-colors font-semibold rounded-lg ${
                      activePage === 'tables' 
                        ? 'text-indigo-600 dark:text-indigo-400 bg-indigo-50 dark:bg-indigo-900/30' 
                        : 'text-gray-400 dark:text-gray-500 hover:bg-gray-50 dark:hover:bg-gray-800'
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <Table2 className="w-4 h-4" />
                      <span>Tables</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <span className="text-xs bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 px-2 py-0.5 rounded-full" title="Available tables">
                        {availableTablesCount}
                      </span>
                      <span className="text-gray-400">/</span>
                      <span className="text-xs bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 px-2 py-0.5 rounded-full" title="Occupied tables">
                        {occupiedTablesCount}
                      </span>
                    </div>
                  </a>
                  )}
                </div>
              )}
            </div>
          )}

          {hasEffectivePermission(user, 'reviews', true) && menuVisibility.reviews && (
            <a
              href="#"
              onClick={(e) => { e.preventDefault(); handleNavigation('reviews'); }}
              className={`group relative flex items-center gap-3 px-4 py-3 rounded-lg border-l-4 ${
                activePage === 'reviews'
                  ? 'text-indigo-600 dark:text-indigo-400 border-indigo-600'
                  : 'text-gray-400 dark:text-gray-500 border-transparent hover:bg-gray-50 dark:hover:bg-gray-800 hover:text-gray-700 dark:hover:text-gray-300'
              } transition-colors`}
            >
              <Star className="w-5 h-5" />
              <span className="font-semibold text-sm">Reviews</span>
            </a>
          )}

          {hasEffectivePermission(user, 'messaging', true) && menuVisibility.messaging && (
            <div>
              {/* Main Messaging Button */}
              <button
                onClick={() => setMessagingExpanded(!messagingExpanded)}
                className={`group relative w-full flex items-center gap-3 px-4 py-3 rounded-lg border-l-4 ${
                  activePage === 'message-batches' ||
                  activePage === 'message-templates' ||
                  activePage === 'providers'
                    ? 'text-indigo-600 dark:text-indigo-400 border-indigo-600'
                    : 'text-gray-400 dark:text-gray-500 border-transparent hover:bg-gray-50 dark:hover:bg-gray-800 hover:text-gray-700 dark:hover:text-gray-300'
                } transition-colors`}
              >
                <Mail className="w-5 h-5" />
                <span className="font-semibold text-sm">Messaging</span>
                <ChevronRight
                  className={`w-4 h-4 ml-auto transition-transform ${
                    messagingExpanded ? 'rotate-90' : ''
                  }`}
                />
              </button>

              {/* Sub Menu */}
              {messagingExpanded && (
                <div className="ml-12 mt-1 space-y-1">

                  {/* Batches */}
                  <a
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      handleNavigation('message-batches');
                    }}
                    className={`group relative flex items-center gap-2 px-4 py-2 text-sm font-semibold rounded-lg ${
                      activePage === 'message-batches'
                        ? 'text-indigo-600 dark:text-indigo-400 bg-indigo-50 dark:bg-indigo-900/30'
                        : 'text-gray-400 dark:text-gray-500 hover:bg-gray-50 dark:hover:bg-gray-800 hover:text-gray-700 dark:hover:text-gray-300'
                    }`}
                  >
                    <FileText className="w-4 h-4" />
                    <span>Batches</span>
                  </a>

                  {/* Providers */}
                  <a
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      handleNavigation('message-templates');
                    }}
                    className={`group relative flex items-center gap-2 px-4 py-2 text-sm font-semibold rounded-lg ${
                      activePage === 'message-templates'
                        ? 'text-indigo-600 dark:text-indigo-400 bg-indigo-50 dark:bg-indigo-900/30'
                        : 'text-gray-400 dark:text-gray-500 hover:bg-gray-50 dark:hover:bg-gray-800 hover:text-gray-700 dark:hover:text-gray-300'
                    }`}
                  >
                    <FileText className="w-4 h-4" />
                    <span>Templates</span>
                  </a>

                  <a
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      handleNavigation('providers');
                    }}
                    className={`group relative flex items-center gap-2 px-4 py-2 text-sm font-semibold rounded-lg ${
                      activePage === 'providers'
                        ? 'text-indigo-600 dark:text-indigo-400 bg-indigo-50 dark:bg-indigo-900/30'
                        : 'text-gray-400 dark:text-gray-500 hover:bg-gray-50 dark:hover:bg-gray-800 hover:text-gray-700 dark:hover:text-gray-300'
                    }`}
                  >
                    <Settings className="w-4 h-4" />
                    <span>Providers</span>
                  </a>
                </div>
              )}
            </div>
          )}

          {hasEffectivePermission(user, 'analytics', true) && menuVisibility.analytics && (
            <a
              href="#"
              onClick={(e) => { e.preventDefault(); handleNavigation('analytics'); }}
              className={`group relative flex items-center gap-3 px-4 py-3 rounded-lg border-l-4 ${
                activePage === 'analytics'
                  ? 'text-indigo-600 dark:text-indigo-400 border-indigo-600'
                  : 'text-gray-400 dark:text-gray-500 border-transparent hover:bg-gray-50 dark:hover:bg-gray-800 hover:text-gray-700 dark:hover:text-gray-300'
              } transition-colors`}
            >
              <BarChart3 className="w-5 h-5" />
              <span className="font-semibold text-sm">Analytics</span>
              <ChevronRight className="w-4 h-4 ml-auto" />
            </a>
          )}

          {hasEffectivePermission(user, 'reports', true) && menuVisibility.reports && (
            <a
              href="#"
              onClick={(e) => { e.preventDefault(); handleNavigation('reports'); }}
              className={`group relative flex items-center gap-3 px-4 py-3 rounded-lg border-l-4 ${
                activePage === 'reports'
                  ? 'text-indigo-600 dark:text-indigo-400 border-indigo-600'
                  : 'text-gray-400 dark:text-gray-500 border-transparent hover:bg-gray-50 dark:hover:bg-gray-800 hover:text-gray-700 dark:hover:text-gray-300'
              } transition-colors`}
            >
              <FileText className="w-5 h-5" />
              <span className="font-semibold text-sm">Reports</span>
              <ChevronRight className="w-4 h-4 ml-auto" />
            </a>
          )}

          {hasEffectivePermission(user, 'users', true) && menuVisibility.users && (
            <a
              href="#"
              onClick={(e) => { e.preventDefault(); handleNavigation('users'); }}
              className={`group relative flex items-center gap-3 px-4 py-3 rounded-lg border-l-4 ${
                activePage === 'users'
                  ? 'text-indigo-600 dark:text-indigo-400 border-indigo-600'
                  : 'text-gray-400 dark:text-gray-500 border-transparent hover:bg-gray-50 dark:hover:bg-gray-800 hover:text-gray-700 dark:hover:text-gray-300'
              } transition-colors`}
            >
              <UserCog className="w-5 h-5" />
              <span className="font-semibold text-sm">Users</span>
              <ChevronRight className="w-4 h-4 ml-auto" />
            </a>
          )}

          {hasEffectivePermission(user, 'roles', true) && menuVisibility.users && (
            <a
              href="#"
              onClick={(e) => { e.preventDefault(); handleNavigation('rbac'); }}
              className={`group relative flex items-center gap-3 px-4 py-3 rounded-lg border-l-4 ${
                activePage === 'rbac'
                  ? 'text-indigo-600 dark:text-indigo-400 border-indigo-600'
                  : 'text-gray-400 dark:text-gray-500 border-transparent hover:bg-gray-50 dark:hover:bg-gray-800 hover:text-gray-700 dark:hover:text-gray-300'
              } transition-colors`}
            >
              <Shield className="w-5 h-5" />
              <span className="font-semibold text-sm">Access Control</span>
              <ChevronRight className="w-4 h-4 ml-auto" />
            </a>
          )}

          {hasEffectivePermission(user, 'tenants_platform') && (
            <a
              href="#"
              onClick={(e) => { e.preventDefault(); handleNavigation('tenants'); }}
              className={`group relative flex items-center gap-3 px-4 py-3 rounded-lg border-l-4 ${
                activePage === 'tenants'
                  ? 'text-indigo-600 dark:text-indigo-400 border-indigo-600'
                  : 'text-gray-400 dark:text-gray-500 border-transparent hover:bg-gray-50 dark:hover:bg-gray-800 hover:text-gray-700 dark:hover:text-gray-300'
              } transition-colors`}
            >
              <Building2 className="w-5 h-5" />
              <span className="font-semibold text-sm">Tenants</span>
              <ChevronRight className="w-4 h-4 ml-auto" />
            </a>
          )}

          {hasEffectivePermission(user, 'settings', true) && (
            <a
              href="#"
              onClick={(e) => { e.preventDefault(); handleNavigation('settings'); }}
              className={`group relative flex items-center gap-3 px-4 py-3 rounded-lg border-l-4 ${
                activePage === 'settings'
                  ? 'text-indigo-600 dark:text-indigo-400 border-indigo-600'
                  : 'text-gray-400 dark:text-gray-500 border-transparent hover:bg-gray-50 dark:hover:bg-gray-800 hover:text-gray-700 dark:hover:text-gray-300'
              } transition-colors`}
            >
              <Settings className="w-5 h-5" />
              <span className="font-semibold text-sm">Settings</span>
              <ChevronRight className="w-4 h-4 ml-auto" />
            </a>
          )}

          <a
            href="#"
            onClick={(e) => { e.preventDefault(); handleNavigation('logout'); }}
            className={`group relative flex items-center gap-3 px-4 py-3 rounded-lg border-l-4 ${
              activePage === 'logout'
                ? 'text-indigo-600 dark:text-indigo-400 border-indigo-600'
                : 'text-gray-400 dark:text-gray-500 border-transparent hover:bg-gray-50 dark:hover:bg-gray-800 hover:text-gray-700 dark:hover:text-gray-300'
            } transition-colors`}
          >
            <X className="w-5 h-5" />
            <span className="font-semibold text-sm">Logout</span>
            <ChevronRight className="w-4 h-4 ml-auto" />
          </a>

          <div className="pt-8 pb-2">
            <div className="relative">
              <div className="absolute -top-8 left-1/2 -translate-x-1/2 z-10">
                <img
                  src="https://images.unsplash.com/photo-1555939594-58d7cb561ad1?w=200&h=200&fit=crop"
                  alt="Food"
                  className="w-16 h-16 object-cover rounded-full border-4 border-white shadow-lg"
                />
              </div>
              <div className="bg-gradient-to-br from-indigo-500 via-indigo-600 to-purple-600 rounded-3xl pt-12 pb-5 px-5 text-white text-center relative overflow-hidden shadow-lg">
                <div className="relative z-10">
                  <p className="text-xs mb-3 leading-relaxed font-normal opacity-95">
                    Organize your menus through button below
                  </p>
                  <button 
                    onClick={() => handleNavigation('menuTypes')}
                    className="bg-white text-indigo-600 px-4 py-2 rounded-xl text-xs font-semibold hover:bg-gray-100 w-full flex items-center justify-center gap-2 shadow-sm transition-all hover:shadow-md"
                  >
                    <Plus className="w-3.5 h-3.5" />
                    +Add Menus
                  </button>
                </div>
              </div>
            </div>
            
            <div className="text-center space-y-0.5 mt-3">
              {user ? (
                <>
                  <p className="text-xs text-gray-900 dark:text-white font-semibold">
                    {user.name || user.email || 'User'}
                  </p>
                  <p className="text-[10px] text-gray-500 dark:text-gray-400 font-normal capitalize">
                    {user.role || 'Admin'}
                  </p>
                </>
              ) : (
                <p className="text-[10px] text-gray-500 font-normal">
                  Qrypt POS Dashboard
                </p>
              )}
              <p className="text-[10px] text-gray-400 font-normal">
                © 2026 All Rights Reserved
              </p>
              <p className="text-[10px] text-gray-500 font-normal">
                Made with <span className="text-red-500">❤</span> by Qrypt
              </p>
            </div>
          </div>
        </nav>

        {/* About, Tasks, Help - Only visible on smaller screens (hidden on lg+) */}
        {/* Positioned at absolute bottom of sidebar */}
        <div className="lg:hidden border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900">
          <div className="px-4 py-3 space-y-1">
            <a
              href="#"
              onClick={(e) => { e.preventDefault(); handleNavigation('about'); }}
              className={`flex items-center gap-3 px-3 py-2 rounded-lg transition-colors ${
                activePage === 'about'
                  ? 'text-indigo-600 dark:text-indigo-400 bg-indigo-50 dark:bg-indigo-900/30'
                  : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800 hover:text-gray-900 dark:hover:text-gray-200'
              }`}
            >
              <Info className="w-4 h-4" />
              <span className="text-sm font-medium">About</span>
            </a>

            <a
              href="#"
              onClick={(e) => { e.preventDefault(); handleNavigation('tasks'); }}
              className={`flex items-center gap-3 px-3 py-2 rounded-lg transition-colors ${
                activePage === 'tasks'
                  ? 'text-indigo-600 dark:text-indigo-400 bg-indigo-50 dark:bg-indigo-900/30'
                  : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800 hover:text-gray-900 dark:hover:text-gray-200'
              }`}
            >
              <CheckSquare className="w-4 h-4" />
              <span className="text-sm font-medium">Tasks</span>
            </a>

            <a
              href="#"
              onClick={(e) => { e.preventDefault(); handleNavigation('help'); }}
              className={`flex items-center gap-3 px-3 py-2 rounded-lg transition-colors ${
                activePage === 'help'
                  ? 'text-indigo-600 dark:text-indigo-400 bg-indigo-50 dark:bg-indigo-900/30'
                  : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800 hover:text-gray-900 dark:hover:text-gray-200'
              }`}
            >
              <HelpCircle className="w-4 h-4" />
              <span className="text-sm font-medium">Help</span>
            </a>
          </div>
        </div>

        <div className="h-4"></div>
      </div>
    </>
  );
}